import { CheckCircle2, XCircle, MinusCircle, Clock, CalendarClock } from "lucide-react";

// Owner-only observability card for the external cron scheduler. Reads
// /admin/cron-jobs and renders one row per registered job with its last
// attempt, last success, current status, runtime, and truncated error (if any).
// Retention is naturally one attempt per job — the backend upserts on _id.

const STATUS_META = {
  success:   { label: "success",   pill: "bg-emerald-50 text-emerald-700 border-emerald-100", Icon: CheckCircle2 },
  failed:    { label: "failed",    pill: "bg-red-50 text-red-700 border-red-100",             Icon: XCircle },
  skipped:   { label: "skipped",   pill: "bg-amber-50 text-amber-700 border-amber-100",       Icon: MinusCircle },
  never_run: { label: "never run", pill: "bg-charcoal-50 text-charcoal-500 border-charcoal-100", Icon: Clock },
};

function relTime(iso) {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
    return `${Math.round(secs / 86400)}d ago`;
  } catch { return "—"; }
}

function fmtDuration(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

export function CronJobsCard({ jobs }) {
  if (!jobs || !jobs.length) return null;

  return (
    <section
      className="mt-4 rounded-2xl bg-white border border-black/5 shadow-soft overflow-hidden"
      data-testid="cron-jobs-card"
    >
      <div className="flex items-center gap-2 px-5 py-4 border-b border-black/5">
        <CalendarClock className="w-4 h-4 text-charcoal-500" />
        <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">
          Cron jobs
        </p>
        <p className="ml-2 text-xs font-mono text-charcoal-400">
          external scheduler · last attempt per job
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead className="bg-charcoal-50/50 text-charcoal-500">
            <tr>
              <th className="text-left px-5 py-2 font-normal">Job</th>
              <th className="text-left px-3 py-2 font-normal">Status</th>
              <th className="text-left px-3 py-2 font-normal">Last attempt</th>
              <th className="text-left px-3 py-2 font-normal">Last success</th>
              <th className="text-left px-3 py-2 font-normal">Runtime</th>
              <th className="text-left px-5 py-2 font-normal">Last error</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => {
              const meta = STATUS_META[j.last_status] || STATUS_META.never_run;
              const Icon = meta.Icon;
              return (
                <tr
                  key={j.job}
                  className="border-t border-black/5"
                  data-testid={`cron-job-row-${j.job}`}
                >
                  <td className="px-5 py-2 text-charcoal-800">
                    {j.label}
                    <div className="text-[10px] text-charcoal-400">{j.job}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${meta.pill}`}
                      data-testid={`cron-job-status-${j.job}`}
                    >
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-charcoal-600" title={j.last_attempt_at || ""}>
                    {relTime(j.last_attempt_at)}
                  </td>
                  <td className="px-3 py-2 text-charcoal-600" title={j.last_success_at || ""}>
                    {relTime(j.last_success_at)}
                  </td>
                  <td className="px-3 py-2 text-charcoal-600">{fmtDuration(j.duration_ms)}</td>
                  <td className="px-5 py-2 text-red-500 max-w-[280px] truncate" title={j.last_error || ""}>
                    {j.last_error || "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
